/*
 * Determine if a HEX value is light or dark.
 */
export const contrast = hex => {
  if (!hex || hex === '#fff' || hex === '#ffffff') {
    return 'light'
  }

  // Solid black seems to mess with it, let's just dip early
  if (hex === '#000000') {
    return 'dark'
  }

  const { r, g, b } = hexToRGB(hex)

  const yiq = (r * 299 + g * 587 + b * 114) / 1000

  return yiq >= 128 ? 'light' : 'dark'
}

/*
 * Convert a HEX value to an RGB value.
 *
 * @returns {{ r, g, b}}
 */
export const hexToRGB = hex => {
  const m = hex.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i) || []

  const rgb = {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  }

  return { ...rgb, value: toString(rgb) }
}

/*
 * Convert an RGB value to a HEX value.
 *
 * @returns {hex}
 */
export const rgbToHex = (r, g, b) => {
  // Convert RGB component to 2 digit hex
  const base10to16 = val => {
    const str = `0${val.toString(16)}`
    return str.slice(-2)
  }
  const hex = `#${base10to16(r)}${base10to16(g)}${base10to16(b)}`

  return hex
}

/*
 * Convert an RGB Object to a CMYK Object.
 *
 * @returns {{ c, m, y, k }}
 */
export const rgbToCMYK = ({ r, g, b }) => {
  let [c, m, y, k] = [0, 0, 0, 0]

  r /= 255
  g /= 255
  b /= 255

  k = Math.min(1 - r, 1 - g, 1 - b)
  c = (1 - r - k) / (1 - k)
  m = (1 - g - k) / (1 - k)
  y = (1 - b - k) / (1 - k)

  const cmyk = {
    c: Math.round(c * 100) || 0,
    m: Math.round(m * 100) || 0,
    y: Math.round(y * 100) || 0,
    k: Math.round(k * 100) || 0,
  }

  return { ...cmyk, value: toString(cmyk) }
}

/*
 * Compare two HEX values and calculate teh WCAG contrast ratio
 * Ref: https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o
 */
export const contrastRatio = (hex1, hex2) => {
  const { r: r1, g: g1, b: b1 } = hexToRGB(hex1)
  const { r: r2, g: g2, b: b2 } = hexToRGB(hex2)

  // Calculate luminance
  const l1 = luminance(r1, g1, b1)
  const l2 = luminance(r2, g2, b2)

  const ratio = l1 > l2 ? (l2 + 0.05) / (l1 + 0.05) : (l1 + 0.05) / (l2 + 0.05)

  return 1 / ratio
}

/*
 * Calculate relative luminance
 */
export const luminance = (r, g, b) => {
  const a = [r, g, b].map(v => {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
}

/*
 * Invert a hex value
 */
export const invertHex = hex => {
  const { r, g, b } = hexToRGB(hex)

  return rgbToHex(255 - r, 255 - g, 255 - b)
}

const toString = obj => Object.values(obj).join(' ')

export const convertLinearGradientObjectToCSS = gradient => {
  const { stops } = gradient
  const stopStrings = stops.map(s => `${s.color}`)
  const stopString = stopStrings.join(',')

  return `linear-gradient(to right, ${stopString})`
}
